import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowRight, faClock, faTriangleExclamation } from '@/fontawesome';
import type { EuroCent, SubscriptionPriceProduct, SubscriptionType } from '@/models';
import type { LandingPackageConfig } from '@/consts/pricingPublic';

export type BillingPeriod = '1m' | '6m' | '12m';

interface PublicPricingCardProps {
  product: SubscriptionPriceProduct;
  billingPeriod: BillingPeriod;
  config: LandingPackageConfig;
  monthlyBasePrice?: EuroCent;
  onSelectPlan: (type: SubscriptionType) => void;
  periodLabel?: string;
  periodLabelSuffix?: string;
}

const NEUTRAL_STYLE = {
  border: 'border-gray-200 dark:border-gray-700',
  name: 'text-gray-900 dark:text-white',
  check: 'text-gray-500 dark:text-gray-400',
  tagBg: 'bg-gray-50 dark:bg-gray-700/40',
  tagText: 'text-gray-700 dark:text-gray-300',
  topBar: 'bg-gray-300 dark:bg-gray-600',
};

function calculatePricing(price: EuroCent, period: BillingPeriod, monthlyBasePrice?: EuroCent) {
  const periodMonths = period === '6m' ? 6 : period === '12m' ? 12 : 1;
  const pricePerMonth = price / periodMonths / 100;
  const basePricePerMonth = monthlyBasePrice !== undefined ? monthlyBasePrice / 100 : pricePerMonth;
  const savings = monthlyBasePrice !== undefined ? (monthlyBasePrice * periodMonths - price) / 100 : 0;

  return {
    pricePerMonth,
    basePricePerMonth,
    priceTotal: price / 100,
    periodMonths,
    savings,
  };
}

export default function PublicPricingCard({
  product,
  billingPeriod,
  config,
  monthlyBasePrice,
  onSelectPlan,
  periodLabel,
  periodLabelSuffix,
}: PublicPricingCardProps) {
  const accent = NEUTRAL_STYLE;
  const pricing = calculatePricing(product.price, billingPeriod, monthlyBasePrice);

  const formatEuro = (v: number): string => v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const periodCopy = config.periodCopy?.[billingPeriod];
  const headline = periodCopy?.headline ?? config.headline;
  const hookLines = periodCopy?.hookLines ?? config.hookLines;
  const transitionLine = periodCopy?.transitionLine ?? config.transitionLine;
  const tagline = periodCopy?.tagline ?? config.tagline;
  const scheduleInfo = periodCopy?.scheduleInfo ?? config.scheduleInfo;
  const baseFeatures = periodCopy?.features ?? config.features;
  const features = periodCopy?.appendSavingsFeature === true && pricing.savings > 0
    ? [...baseFeatures, `Du sparst ${formatEuro(pricing.savings)} €`]
    : baseFeatures;

  const badgeLabel = periodCopy?.badgeLabel ?? (config.popular === true ? 'Am beliebtesten' : undefined);
  const highlighted = badgeLabel !== undefined;

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 rounded-xl border overflow-hidden transition-all shadow-md hover:shadow-xl hover:-translate-y-1 flex flex-col md:grid md:grid-rows-subgrid md:row-span-6 ${
        highlighted
          ? 'border-gray-900 dark:border-white border-2 shadow-xl shadow-black/10 dark:shadow-white/10'
          : `${accent.border}`
      }`}
    >
      {/* Row 1: Accent Bar */}
      <div className={`h-1 ${highlighted ? 'bg-gray-900 dark:bg-white' : accent.topBar}`} />

      {/* Popular Badge (absolute, doesn't affect layout) */}
      {highlighted && (
        <div className="absolute top-3 right-4">
          <span className="text-[10px] font-bold uppercase tracking-wider text-white dark:text-gray-900 bg-gray-900 dark:bg-white px-2.5 py-1 rounded-full">
            {badgeLabel}
          </span>
        </div>
      )}

      {/* Row 2: Header (Name, Headline, Hook, Transition, Schedule) */}
      <div className="px-6 pt-5">
        {periodLabel !== undefined && (
          <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <span>{periodLabel}</span>
            {periodLabelSuffix !== undefined && (
              <span className="text-[10px] font-bold">{periodLabelSuffix}</span>
            )}
          </div>
        )}
        <p className={`text-xs font-bold uppercase tracking-widest ${accent.name} mb-3`}>
          {config.label}
        </p>

        <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-snug mb-3">
          {headline}
        </h3>

        <div className="mb-3">
          {hookLines.map((line, idx) => (
            <p key={idx} className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              {line}
            </p>
          ))}
        </div>

        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          {transitionLine}
        </p>

        {scheduleInfo !== undefined && (
          <div className="flex items-center gap-2 mt-4 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
            <FontAwesomeIcon icon={faClock} className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{scheduleInfo}</span>
          </div>
        )}
      </div>

      {/* Row 3: Divider + Features */}
      <div className="px-6 pt-5">
        <div className="w-full h-px bg-gray-100 dark:bg-gray-700 mb-5" />
        <ul className="space-y-2.5">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2.5">
              <FontAwesomeIcon icon={faCheck} className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${accent.check}`} />
              <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Row 4: Tagline + Warnings */}
      <div className="px-6 pt-5 flex flex-col">
        <div className={`flex-1 flex items-center rounded-lg px-4 py-2.5 ${accent.tagBg}`}>
          <p className={`text-sm font-semibold ${accent.tagText}`}>
            {tagline}
          </p>
        </div>

        {config.warnings !== undefined && (
          <div className="space-y-1.5 mt-4">
            {config.warnings.map((warning, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="w-3 h-3 text-amber-500 dark:text-amber-400 shrink-0" />
                <span className="text-xs text-gray-500 dark:text-gray-400">{warning}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Row 5: Price block */}
      <div className="px-6 pt-5">
        {billingPeriod !== '1m' && pricing.savings > 0 && (
          <div className="text-sm text-gray-400 dark:text-gray-500 line-through mb-0.5">
            {formatEuro(pricing.basePricePerMonth)} € / Monat
          </div>
        )}
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatEuro(pricing.pricePerMonth)} €
          </span>
          <span className="text-sm text-gray-400 dark:text-gray-500">/ Monat</span>
        </div>
        {pricing.savings > 0 && periodCopy?.appendSavingsFeature !== true && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Du sparst {formatEuro(pricing.savings)} €
          </p>
        )}
        {billingPeriod !== '1m' && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {formatEuro(pricing.priceTotal)} € alle {pricing.periodMonths} Monate
          </p>
        )}
      </div>

      {/* Row 6: CTA + fine print */}
      <div className="px-6 pt-5 pb-6 flex flex-col">
        <button
          onClick={() => onSelectPlan(product.type)}
          className="w-full flex-1 py-3 px-4 rounded-lg font-semibold text-white shadow-lg transition-all transform bg-linear-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer flex items-center justify-center"
        >
          {config.ctaLabel}
          <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4 ml-2" />
        </button>
        <p className="text-[11px] text-center text-gray-400 dark:text-gray-500 mt-2">
          Jederzeit kündbar. Kein Risiko
        </p>
      </div>
    </div>
  );
}
